/**
 * Utility functions to convert between camelCase (frontend) and snake_case (backend)
 */

/**
 * Convert a string from camelCase to snake_case
 */
export const camelToSnake = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

/**
 * Convert a string from snake_case to camelCase
 */
export const snakeToCamel = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Convert an object's keys from camelCase to snake_case (recursive)
 */
export const camelToSnakeCase = <T extends Record<string, any>>(obj: T): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item: any) => camelToSnakeCase(item));
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  const converted: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const snakeKey = camelToSnake(key);
      const value: any = obj[key];
      
      if (value && typeof value === 'object' && value.constructor === Date) {
        converted[snakeKey] = (value as Date).toISOString();
      } else if (Array.isArray(value)) {
        converted[snakeKey] = value.map((item: any) => 
          typeof item === 'object' && item !== null ? camelToSnakeCase(item) : item
        );
      } else if (typeof value === 'object' && value !== null && !(value && typeof value === 'object' && value.constructor === Date)) {
        converted[snakeKey] = camelToSnakeCase(value);
      } else {
        converted[snakeKey] = value;
      }
    }
  }
  return converted;
};

/**
 * Convert an object's keys from snake_case to camelCase (recursive)
 */
export const snakeToCamelCase = <T extends Record<string, any>>(obj: T): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item: any) => snakeToCamelCase(item));
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  const converted: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const camelKey = snakeToCamel(key);
      const value: any = obj[key];
      
      if (value && typeof value === 'object' && value.constructor === Date) {
        converted[camelKey] = value as Date;
      } else if (Array.isArray(value)) {
        converted[camelKey] = value.map((item: any) => 
          typeof item === 'object' && item !== null ? snakeToCamelCase(item) : item
        );
      } else if (typeof value === 'object' && value !== null && !(value && typeof value === 'object' && value.constructor === Date)) {
        converted[camelKey] = snakeToCamelCase(value);
      } else {
        converted[camelKey] = value;
      }
    }
  }
  return converted;
};

/**
 * List of fields that should NOT be converted (e.g., IDs, URLs, etc.)
 */
const EXCLUDE_FIELDS = ['id', 'url', 'uri', 'uuid', 'ndc', 'ndcNumber'];

/**
 * Convert request body from camelCase to snake_case (with exclusions)
 */
export const convertRequestData = (data: any): any => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(convertRequestData);
  }

  const converted: any = {};
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      // Skip conversion for excluded fields
      if (EXCLUDE_FIELDS.some((field) => key.toLowerCase().includes(field.toLowerCase()))) {
        converted[key] = data[key];
      } else {
        const snakeKey = camelToSnake(key);
        const value = data[key];
        
        if (value === null || value === undefined) {
          converted[snakeKey] = value;
        } else if (value && typeof value === 'object' && value.constructor === Date) {
          converted[snakeKey] = (value as Date).toISOString();
        } else if (Array.isArray(value)) {
          converted[snakeKey] = value.map((item: any) => 
            typeof item === 'object' && item !== null ? convertRequestData(item) : item
          );
        } else if (typeof value === 'object') {
          converted[snakeKey] = convertRequestData(value);
        } else {
          converted[snakeKey] = value;
        }
      }
    }
  }
  return converted;
};

/**
 * Convert FormData field names from camelCase to snake_case
 */
export const convertFormData = (formData: Record<string, any>): FormData => {
  const fd = new FormData();
  
  for (const key in formData) {
    if (Object.prototype.hasOwnProperty.call(formData, key)) {
      const value = formData[key];
      const snakeKey = camelToSnake(key);
      
      if (value === null || value === undefined) {
        fd.append(snakeKey, '');
      } else if (value instanceof File || value instanceof Blob) {
        fd.append(snakeKey, value);
      } else if (typeof value === 'object' && !(value instanceof Date)) {
        fd.append(snakeKey, JSON.stringify(value));
      } else {
        fd.append(snakeKey, String(value));
      }
    }
  }
  
  return fd;
};

/**
 * Convert response data from snake_case to camelCase
 */
export const convertResponseData = (data: any): any => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(convertResponseData);
  }

  const converted: any = {};
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const camelKey = snakeToCamel(key);
      const value = data[key];
      
      if (value === null || value === undefined) {
        converted[camelKey] = value;
      } else if ((value && typeof value === 'object' && value.constructor === Date) || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value))) {
        // Handle date strings
        converted[camelKey] = value;
      } else if (Array.isArray(value)) {
        converted[camelKey] = value.map((item: any) => 
          typeof item === 'object' && item !== null ? convertResponseData(item) : item
        );
      } else if (typeof value === 'object') {
        converted[camelKey] = convertResponseData(value);
      } else {
        converted[camelKey] = value;
      }
    }
  }
  return converted;
};

/**
 * Prefer camelCase (API interceptor output) with snake_case fallback for legacy payloads.
 * Use this instead of `obj.foo || obj.foo_bar` scattered in components.
 */
export function pickField<T = unknown>(
  obj: Record<string, unknown> | null | undefined,
  camel: string,
  snake?: string
): T | undefined {
  if (!obj) return undefined;
  if (camel in obj && obj[camel] !== undefined && obj[camel] !== null) {
    return obj[camel] as T;
  }
  if (snake && snake in obj && obj[snake] !== undefined && obj[snake] !== null) {
    return obj[snake] as T;
  }
  return undefined;
}

/** Canonical facility id from dual-cased user/facility objects. */
export function getFacilityId(
  entity: { facilityId?: string | null; facility_id?: string | null } | null | undefined
): string | undefined {
  return entity?.facilityId || entity?.facility_id || undefined;
}

